'use client'

import { useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations, useLocale } from 'next-intl'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import styles from '@/styles/coming-soon.module.css'
import { BreadcrumbSchema } from '@/lib/schema'
import Link from 'next/link'
import { useActivityVisibility } from '@/hooks/useActivityVisibility'

const LODGE_ID = 'apex-lakefront-serenity'
const HERO_IMAGE = '/images/lodges/apex-lakefront-serenity/coming-soon/hero-villarrica-lake-volcano.webp'

export default function ApexLakefrontSerenityPage() {
  const t = useTranslations('lodges')
  const tCommon = useTranslations('common')
  const locale = useLocale()
  const { isVisible, loaded } = useActivityVisibility()
  const router = useRouter()
  const heroImgRef = useRef<HTMLImageElement>(null)
  const heroBgRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (loaded && !isVisible(LODGE_ID, true)) {
      router.replace(`/${locale}/lodges`)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded])

  useEffect(() => {
    // Show previous hero as instant placeholder while new one loads
    try {
      const prev = sessionStorage.getItem('apex_last_hero')
      if (prev && (prev.startsWith('/images/') || prev.startsWith('https://')) && heroBgRef.current) {
        heroBgRef.current.style.backgroundImage = `url("${prev}")`
        heroBgRef.current.style.backgroundSize = 'cover'
        heroBgRef.current.style.backgroundPosition = 'center'
      }
    } catch {}

    const el = heroImgRef.current
    if (!el) return

    // Image may already be loaded from cache before onLoad handler was attached
    if (el.complete && el.naturalWidth > 0) {
      el.style.opacity = '1'
      try { sessionStorage.setItem('apex_last_hero', HERO_IMAGE) } catch {}
    }

    const onScroll = () => {
      el.style.transform = `translateY(${window.scrollY * 0.5}px)`
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  if (loaded && !isVisible(LODGE_ID, true)) {
    return null
  }

  // Get translated lodge data
  const lodgeData = {
    name: t('apex-lakefront-serenity.name'),
    subtitle: t('apex-lakefront-serenity.subtitle'),
    location: t('apex-lakefront-serenity.location'),
  }

  // Coming soon page translations
  const cs = {
    badge: t('apex-lakefront-serenity.comingSoon.badge'),
    heroSubtitle: t('apex-lakefront-serenity.comingSoon.heroSubtitle'),
    locationLabel: t('apex-lakefront-serenity.comingSoon.locationLabel'),
    locationTitle: t('apex-lakefront-serenity.comingSoon.locationTitle'),
    locationDescription: t('apex-lakefront-serenity.comingSoon.locationDescription'),
    stat1Label: t('apex-lakefront-serenity.comingSoon.stat1Label'),
    stat2Label: t('apex-lakefront-serenity.comingSoon.stat2Label'),
    stat3Label: t('apex-lakefront-serenity.comingSoon.stat3Label'),
    gallerySectionTitle: t('apex-lakefront-serenity.comingSoon.gallerySectionTitle'),
    galleryCaption1: t('apex-lakefront-serenity.comingSoon.galleryCaption1'),
    galleryCaption2: t('apex-lakefront-serenity.comingSoon.galleryCaption2'),
    galleryCaption3: t('apex-lakefront-serenity.comingSoon.galleryCaption3'),
    ctaTitle: t('apex-lakefront-serenity.comingSoon.ctaTitle'),
    ctaDescription: t('apex-lakefront-serenity.comingSoon.ctaDescription'),
  }

  // Image alt translations
  const alts = {
    heroAlt: t('apex-lakefront-serenity.imageAlts.heroAlt'),
    woodenArchAlt: t('apex-lakefront-serenity.imageAlts.woodenArchAlt'),
    kayakAlt: t('apex-lakefront-serenity.imageAlts.kayakAlt'),
    sunsetGridAlt: t('apex-lakefront-serenity.imageAlts.sunsetGridAlt'),
    mapAlt: t('apex-lakefront-serenity.imageAlts.mapAlt'),
    wakeboardAlt: t('apex-lakefront-serenity.imageAlts.wakeboardAlt'),
    fishingAlt: t('apex-lakefront-serenity.imageAlts.fishingAlt'),
    sunsetGalleryAlt: t('apex-lakefront-serenity.imageAlts.sunsetGalleryAlt'),
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: tCommon('lodges'), href: `/${locale}/lodges/` },
        { name: lodgeData.name, href: `/${locale}/lodges/apex-lakefront-serenity/` },
      ]} />
      <Header />

      <div className={styles.comingSoonPage}>
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div ref={heroBgRef} className={styles.heroBackground}>
            <img
              ref={heroImgRef}
              className={styles.heroImage}
              src={HERO_IMAGE}
              alt={alts.heroAlt}
              fetchPriority="high"
              onLoad={(e) => {
                const img = e.currentTarget as HTMLImageElement
                requestAnimationFrame(() => { img.style.opacity = '1' })
                try { sessionStorage.setItem('apex_last_hero', HERO_IMAGE) } catch {}
              }}
              style={{ opacity: 0, transition: 'opacity 0.5s ease' }}
            />
            <div className={styles.heroOverlay}></div>
          </div>
          <div className={styles.heroContent}>
            <span className={styles.comingSoonBadge}>{cs.badge}</span>
            <h1 className={styles.heroTitle}>{lodgeData.name}</h1>
            <p className={styles.heroSubtitle}>{cs.heroSubtitle}</p>
          </div>
        </section>

        {/* Location Section */}
        <section className={styles.locationSection}>
          <div className="container">
            <div className={styles.locationGrid}>
              <div className={styles.locationInfo}>
                <span className={styles.locationLabel}>{cs.locationLabel}</span>
                <h2 className={styles.locationTitle}>
                  {cs.locationTitle}
                </h2>
                <p className={styles.locationDescription}>
                  {cs.locationDescription}
                </p>
                <div className={styles.statsRow}>
                  <div className={styles.stat}>
                    <span className={styles.statNumber}>227m</span>
                    <span className={styles.statLabel}>{cs.stat1Label}</span>
                  </div>
                  <div className={styles.stat}>
                    <span className={styles.statNumber}>176km²</span>
                    <span className={styles.statLabel}>{cs.stat2Label}</span>
                  </div>
                  <div className={styles.stat}>
                    <span className={styles.statNumber}>2,847m</span>
                    <span className={styles.statLabel}>{cs.stat3Label}</span>
                  </div>
                </div>
              </div>
              <div className={styles.locationImages}>
                <div className={styles.imageGrid}>
                  <img
                    className={styles.gridImage}
                    src="/images/lodges/apex-lakefront-serenity/coming-soon/pucon-wooden-architecture.webp"
                    alt={alts.woodenArchAlt}
                    loading="lazy"
                  />
                  <img
                    className={styles.gridImage}
                    src="/images/lodges/apex-lakefront-serenity/coming-soon/kayak-lake-villarrica.webp"
                    alt={alts.kayakAlt}
                    loading="lazy"
                  />
                  <img
                    className={styles.gridImage}
                    src="/images/lodges/apex-lakefront-serenity/coming-soon/sunset-lake-villarrica.webp"
                    alt={alts.sunsetGridAlt}
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className={styles.mapSection}>
          <div className={styles.mapContent}>
            <div className={styles.mapPlaceholder}>
              <img
                className={styles.mapImage}
                src="/images/lodges/apex-lakefront-serenity/coming-soon/hero-villarrica-lake-volcano.webp"
                alt={alts.mapAlt}
                loading="lazy"
              />
              <div className={styles.mapOverlay}>
                <div className={styles.mapPin}>
                  <svg width="32" height="40" viewBox="0 0 32 40" fill="currentColor">
                    <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24c0-8.837-7.163-16-16-16zm0 22a6 6 0 110-12 6 6 0 010 12z"/>
                  </svg>
                </div>
                <span className={styles.mapLabel}>Pucón, Chile</span>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className={styles.gallerySection}>
          <div className="container">
            <h2 className={styles.gallerySectionTitle}>{cs.gallerySectionTitle}</h2>
            <div className={styles.galleryGrid}>
              <div className={styles.galleryItem}>
                <img
                  src="/images/lodges/apex-lakefront-serenity/coming-soon/wakeboard-lake-villarrica.webp"
                  alt={alts.wakeboardAlt}
                  loading="lazy"
                />
                <span className={styles.galleryCaption}>{cs.galleryCaption1}</span>
              </div>
              <div className={styles.galleryItem}>
                <img
                  src="/images/lodges/apex-lakefront-serenity/coming-soon/fishing-lake-villarrica.webp"
                  alt={alts.fishingAlt}
                  loading="lazy"
                />
                <span className={styles.galleryCaption}>{cs.galleryCaption2}</span>
              </div>
              <div className={styles.galleryItem}>
                <img
                  src="/images/lodges/apex-lakefront-serenity/coming-soon/sunset-lake-villarrica.webp"
                  alt={alts.sunsetGalleryAlt}
                  loading="lazy"
                />
                <span className={styles.galleryCaption}>{cs.galleryCaption3}</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>
              {cs.ctaTitle}
            </h2>
            <p className={styles.ctaDescription}>
              {cs.ctaDescription}
            </p>
            <Link href={`/${locale}/contact`} className={styles.ctaButton}>
              {tCommon('contactUs')}
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </>
  )
}
